import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type {
  BuyCreditPackRequest,
  BuyCreditPackResponse,
  BuyCustomCreditRequest,
  BuyCustomCreditResponse,
  CreditBalance,
  CreditPack,
  CustomCreditPurchaseConfig,
  ListCreditsTransactionsQuery,
  ListCreditsTransactionsResponse,
  MonthlyCreditStats,
} from './types.js';

export class CreditsClient {
  constructor(private readonly http: BaseClient) {}

  async getBalance(): Promise<CreditBalance> {
    const res = await this.http.get<{ success: boolean; data: CreditBalance }>('/credits/balance');
    return unwrapData(res);
  }

  listTransactions(query: ListCreditsTransactionsQuery = {}): Promise<ListCreditsTransactionsResponse> {
    return this.http.get<ListCreditsTransactionsResponse>('/credits/transactions', { query });
  }

  /** Per-month credit activity (consumption / purchases / grants). */
  async getMonthlyStats(): Promise<MonthlyCreditStats[]> {
    const res = await this.http.get<{ success: boolean; data: MonthlyCreditStats[] }>('/credits/stats/monthly');
    return res.data ?? [];
  }

  /** Fixed credit-pack catalog. Public endpoint. */
  async listPacks(): Promise<CreditPack[]> {
    const res = await this.http.get<{ success: boolean; data: CreditPack[] }>('/credits/packs', { skipAuth: true });
    return res.data ?? [];
  }

  /**
   * Buy a fixed credit pack. Requires an active paid subscription
   * (the backend 403s free-tier orgs with SUBSCRIPTION_REQUIRED) and
   * caps purchases per pack per billing cycle. Returns a Stripe
   * Checkout URL to redirect to.
   */
  buyPack(body: BuyCreditPackRequest): Promise<BuyCreditPackResponse> {
    return this.http.post<BuyCreditPackResponse>('/credits/packs/buy', { body });
  }

  /**
   * "Other amount" PAYG config — bounds + presets for the
   * arbitrary-dollar tile. Public endpoint; returns a disabled
   * shape (`enabled: false`) when the feature is off so the
   * frontend can hide the tile without throwing.
   */
  async getCustomConfig(): Promise<CustomCreditPurchaseConfig> {
    const res = await this.http.get<{ success: boolean; data: CustomCreditPurchaseConfig }>('/credits/packs/custom-config');
    return unwrapData(res);
  }

  /**
   * Buy an arbitrary-dollar PAYG amount. The Stripe Checkout
   * session is built with `price_data` inline (no pre-created
   * Price), so the catalog has nothing to provision for this — it
   * is purely a per-purchase flow.
   */
  buyCustom(body: BuyCustomCreditRequest): Promise<BuyCustomCreditResponse> {
    return this.http.post<BuyCustomCreditResponse>('/credits/packs/buy-custom', { body });
  }
}
